package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.scoring.entity.LeaderboardEntry
import com.fantasytenis.web.hooks.isMobile
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.ThemeColors
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Compact leaderboard card showing entries around the current user.
 * Used on GameDayScreen to provide quick leaderboard context.
 */
/**
 * Callback for rank change events (for toast notifications)
 */
data class RankChangeEvent(
    val userId: String,
    val displayName: String,
    val oldRank: Int,
    val newRank: Int,
    val isCurrentUser: Boolean
)

@Composable
fun MiniLeaderboardCard(
    entries: List<LeaderboardEntry>,
    focusedUserId: String?,
    @Suppress("UNUSED_PARAMETER") contestId: String, // Reserved for future use (e.g., deep linking)
    isLive: Boolean,
    onEntryClick: (entryId: String) -> Unit,
    onViewFullClick: () -> Unit,
    onRankChange: ((RankChangeEvent) -> Unit)? = null,
    colors: ThemeColors
) {
    val isMobileView = isMobile()
    val scope = rememberCoroutineScope()

    // Track previous ranks for animation detection
    var previousRanks by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }

    // Track which entries are currently animating
    var animatingEntries by remember { mutableStateOf<Map<String, AnimationState>>(emptyMap()) }

    // Detect rank changes and trigger animations
    LaunchedEffect(entries) {
        if (previousRanks.isNotEmpty()) {
            entries.forEach { entry ->
                val prevRank = previousRanks[entry.userId]
                if (prevRank != null && prevRank != entry.rank) {
                    val direction = if (entry.rank < prevRank) AnimationState.MOVING_UP else AnimationState.MOVING_DOWN
                    animatingEntries = animatingEntries + (entry.userId to direction)

                    // Notify callback for toast
                    if (entry.userId == focusedUserId) {
                        onRankChange?.invoke(
                            RankChangeEvent(
                                userId = entry.userId,
                                displayName = entry.displayName,
                                oldRank = prevRank,
                                newRank = entry.rank,
                                isCurrentUser = true
                            )
                        )
                    }

                    // Clear animation after delay
                    scope.launch {
                        delay(600) // Match CSS animation duration
                        animatingEntries = animatingEntries - entry.userId
                    }
                }
            }
        }
        previousRanks = entries.associate { it.userId to it.rank }
    }

    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(12.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                H4({
                    style {
                        margin(0.px)
                        fontSize(14.px)
                        fontWeight(700)
                        color(colors.textPrimary)
                    }
                }) {
                    Text("Leaderboard")
                }

                // Live indicator
                if (isLive) {
                    LiveBadge(colors)
                }
            }

            // View full link
            Span({
                style {
                    fontSize(12.px)
                    color(colors.primary)
                    cursor("pointer")
                    fontWeight(500)
                }
                onClick { onViewFullClick() }
            }) {
                Text("View All →")
            }
        }

        // Entries list
        if (entries.isEmpty()) {
            Div({
                style {
                    textAlign("center")
                    padding(16.px)
                    color(colors.textSecondary)
                    fontSize(13.px)
                }
            }) {
                Text("No entries yet")
            }
        } else {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(2.px)
                }
            }) {
                entries.forEach { entry ->
                    val isCurrentUser = entry.userId == focusedUserId
                    val animationState = animatingEntries[entry.userId]

                    Div({
                        // Add animation classes based on state
                        when (animationState) {
                            AnimationState.MOVING_UP -> classes("leaderboard-rank-up")
                            AnimationState.MOVING_DOWN -> classes("leaderboard-rank-down")
                            null -> {}
                        }
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "32px 1fr auto auto")
                            gap(if (isMobileView) 8.px else 12.px)
                            alignItems(AlignItems.Center)
                            padding(8.px, 10.px)
                            borderRadius(8.px)
                            backgroundColor(
                                when {
                                    animationState == AnimationState.MOVING_UP -> Color("rgba(76, 175, 80, 0.15)")
                                    animationState == AnimationState.MOVING_DOWN -> Color("rgba(244, 67, 54, 0.15)")
                                    isCurrentUser -> colors.highlightBackground
                                    else -> Color.transparent
                                }
                            )
                            cursor("pointer")
                            minHeight(44.px) // Touch target
                            property("transition", "background-color 0.3s, transform 0.3s")
                        }
                        entry.entryId?.let { entryId ->
                            onClick { onEntryClick(entryId) }
                        }
                        onMouseOver {
                            if (!isCurrentUser && animationState == null) {
                                (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                                    colors.surfaceVariant.toString()
                            }
                        }
                        onMouseOut {
                            (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                                if (isCurrentUser) colors.highlightBackground.toString() else "transparent"
                        }
                    }) {
                        // Rank badge
                        MiniRankBadge(entry.rank, colors)

                        // Name
                        Div({
                            style {
                                overflow("hidden")
                            }
                        }) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(4.px)
                                }
                            }) {
                                Span({
                                    style {
                                        fontSize(13.px)
                                        fontWeight(if (isCurrentUser) 600 else 500)
                                        color(colors.textPrimary)
                                        property("white-space", "nowrap")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                    }
                                }) {
                                    Text(entry.displayName)
                                }
                                if (isCurrentUser) {
                                    Span({
                                        style {
                                            fontSize(10.px)
                                            color(colors.highlightText)
                                            fontWeight(500)
                                        }
                                    }) {
                                        Text("(You)")
                                    }
                                }
                            }
                        }

                        // Points
                        Span({
                            style {
                                fontSize(13.px)
                                fontWeight(600)
                                color(colors.primary)
                                textAlign("right")
                            }
                        }) {
                            Text("${entry.points.toInt()}")
                        }

                        // Rank change
                        RankChangeIndicator(entry.rankChange, colors)
                    }
                }
            }
        }
    }
}

@Composable
private fun MiniRankBadge(rank: Int, colors: ThemeColors) {
    val (bgColor, textColor) = when (rank) {
        1 -> Colors.Medal.GoldBackground to Colors.Medal.GoldText
        2 -> Colors.Medal.SilverBackground to Colors.Medal.SilverText
        3 -> Colors.Medal.BronzeBackground to Colors.Medal.BronzeText
        else -> colors.surfaceVariant to colors.textPrimary
    }

    Div({
        style {
            width(28.px)
            height(28.px)
            borderRadius(50.percent)
            backgroundColor(bgColor)
            color(textColor)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            fontSize(12.px)
            fontWeight(600)
            flexShrink(0)
        }
    }) {
        Text("$rank")
    }
}

@Composable
private fun RankChangeIndicator(change: Int, colors: ThemeColors) {
    Div({
        style {
            minWidth(28.px)
            textAlign("right")
        }
    }) {
        when {
            change > 0 -> {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(500)
                        color(colors.success)
                    }
                }) {
                    Text("+$change")
                }
            }
            change < 0 -> {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(500)
                        color(colors.error)
                    }
                }) {
                    Text("$change")
                }
            }
            else -> {
                Span({
                    style {
                        fontSize(11.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("-")
                }
            }
        }
    }
}

@Composable
private fun LiveBadge(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            backgroundColor(colors.successLight)
            padding(2.px, 6.px)
            borderRadius(10.px)
        }
    }) {
        // Pulsing dot
        Div({
            style {
                width(6.px)
                height(6.px)
                borderRadius(50.percent)
                backgroundColor(colors.success)
                property("animation", "pulse 2s infinite")
            }
        })
        Span({
            style {
                fontSize(10.px)
                color(colors.successDark)
                fontWeight(600)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text("Live")
        }
    }
}

/**
 * Animation state for leaderboard entries
 */
private enum class AnimationState {
    MOVING_UP,
    MOVING_DOWN
}
